import axios from '../utils/axios';

/**
 * Nimbus recording episode (episodes table) — the slice of the wire type
 * the client consumes. live_state is derived server-side:
 * 'live' | 'overdue' | 'idle' | 'stale' | 'processing' | 'finished' | 'failed'.
 */
export interface Episode {
  id: string;
  device_id?: string | null;
  live_state: string;
  start_ts_ms: number;
  end_ts_ms?: number | null;
  testsession_id?: string | null;
  // On-demand full-.db upload request: null = never requested;
  // 'requested' (open), 'fulfilled' (file ingested), 'denied' (user declined
  // on the phone). Set via PATCH; 'fulfilled' is server-written only.
  db_upload_state?: 'requested' | 'fulfilled' | 'denied' | null;
  db_upload_requested_at?: string | null;
  db_upload_resolved_at?: string | null;
}

/** Episodes bound to a session, newest first (server orders by start desc). */
export async function getSessionEpisodes(sessionId: string): Promise<Episode[]> {
  return axios.get('/episodes', { params: { testsession_id: sessionId } }).then((res) => res.data);
}

/**
 * Set (or clear) the episode's .db upload request.
 * 'requested' asks the phone to upload on next app start; null cancels an
 * open request; 'denied' is normally set by the phone, not the web.
 */
export async function setEpisodeDbUpload(
  episodeId: string,
  state: 'requested' | 'denied' | null,
): Promise<Episode> {
  return axios.patch(`/episodes/${episodeId}`, { db_upload_state: state }).then((res) => res.data);
}

/** Batch form of setEpisodeDbUpload — one PATCH request for many episodes. */
export async function setEpisodeDbUploadBatch(
  episodeIds: string[],
  state: 'requested' | null,
): Promise<Episode[]> {
  const bodies = episodeIds.map((id) => ({ id, db_upload_state: state }));
  return axios.patch('/episodes', bodies).then((res) => res.data);
}

/**
 * Resolve the episode behind a run: prefer the explicit source_episode_id
 * (chunk-sourced runs), else find the single session episode whose device
 * time window contains the run start. Returns null when no unique episode
 * can be identified — callers should point the user at the session-level
 * recordings card in that case.
 */
export async function resolveRunEpisode(
  run: { source_episode_id?: string | null; run_ts?: string | null },
  sessionId?: string | null,
): Promise<string | null> {
  if (run.source_episode_id) return run.source_episode_id;
  if (!sessionId || !run.run_ts) return null;
  const tsMs = Date.parse(run.run_ts);
  if (Number.isNaN(tsMs)) return null;
  const episodes: Episode[] = await axios
    .get('/episodes', { params: { testsession_id: sessionId, time_start: tsMs, time_finish: tsMs } })
    .then((res) => res.data);
  return episodes.length === 1 ? episodes[0].id : null;
}
